// src/components/Cards/CardConfiguration/CardConfigEvent/DescargarProgramacionPdfModal.jsx
// FIX-EMERGENCIA: descarga masiva de programación PDF (varios deportes colectivos + fase de
// grupos/fase final), solo visible para rol 1. Reutiliza /menu_get_sports (ya filtrado por
// evento/compañía/rol) para listar deportes y POST /programacion-bulk-pdf para generar el archivo.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import Modal from "@mui/joy/Modal";
import Sheet from "@mui/joy/Sheet";
import Typography from "@mui/joy/Typography";
import ModalClose from "@mui/joy/ModalClose";
import { toast } from "react-hot-toast";
import API_BASE_URL from "../../../../config/config.js";
import { getMenuSportsForEvent } from "../../../../services/menuSportsService.js";

const COLLECTIVE_TYPE_SPORT = 1;

const emptySelection = () => ({ groups: false, finals: false });

const DescargarProgramacionPdfModal = ({ open, onClose, idevent }) => {
  const { user } = useSelector((state) => state.auth);

  const [sports, setSports] = useState([]);
  const [loadingSports, setLoadingSports] = useState(false);
  const [selections, setSelections] = useState({}); // idsport -> { groups, finals }
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open || !idevent || !user?.iduser) return;
    let cancelled = false;

    setLoadingSports(true);
    setSelections({});

    getMenuSportsForEvent({ iduser: user.iduser, idevent })
      .then((allSports) => {
        if (cancelled) return;
        setSports(
          (allSports || []).filter(
            (sport) => Number(sport.typeSport) === COLLECTIVE_TYPE_SPORT
          )
        );
      })
      .catch((error) => {
        console.error("Error cargando deportes del evento:", error);
        if (!cancelled) toast.error("No se pudieron cargar los deportes del evento.");
      })
      .finally(() => {
        if (!cancelled) setLoadingSports(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, idevent, user?.iduser]);

  const selectedCount = useMemo(
    () => Object.values(selections).filter((s) => s?.groups || s?.finals).length,
    [selections]
  );

  const allSelected =
    sports.length > 0 &&
    sports.every((sport) => {
      const sel = selections[sport.idsport];
      return sel?.groups && sel?.finals;
    });

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelections({});
      return;
    }
    const next = {};
    sports.forEach((sport) => {
      next[sport.idsport] = { groups: true, finals: true };
    });
    setSelections(next);
  };

  const toggleSportPhase = (idsport, phase) => {
    setSelections((prev) => {
      const current = prev[idsport] || emptySelection();
      return { ...prev, [idsport]: { ...current, [phase]: !current[phase] } };
    });
  };

  const toggleSportParent = (idsport) => {
    setSelections((prev) => {
      const current = prev[idsport] || emptySelection();
      const next = current.groups || current.finals ? emptySelection() : { groups: true, finals: true };
      return { ...prev, [idsport]: next };
    });
  };

  const closeIfIdle = () => {
    if (downloading) return;
    onClose?.();
  };

  const handleDownload = async () => {
    const payloadSports = sports
      .map((sport) => {
        const sel = selections[sport.idsport];
        if (!sel || (!sel.groups && !sel.finals)) return null;
        return { idsport: Number(sport.idsport), groups: !!sel.groups, finals: !!sel.finals };
      })
      .filter(Boolean);

    if (payloadSports.length === 0) {
      toast("Selecciona al menos un deporte y una fase.", { icon: "⚠️" });
      return;
    }

    setDownloading(true);

    try {
      const { data, headers } = await axios.post(
        `${API_BASE_URL}/programacion-bulk-pdf`,
        { idevent: Number(idevent), sports: payloadSports },
        { responseType: "arraybuffer" }
      );

      const contentType = headers["content-type"] || "";
      const isZip = contentType.startsWith("application/zip");
      if (!contentType.startsWith("application/pdf") && !isZip) {
        toast.error("El servidor no pudo generar la programación.");
        return;
      }

      const disp = headers["content-disposition"];
      let filename = isZip ? "programacion.zip" : "programacion.pdf";
      if (disp) {
        const match = disp.match(/filename="?([^";]+)"?/);
        if (match) filename = match[1];
      }

      const blob = new Blob([data], { type: contentType });
      const blobURL = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobURL;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(blobURL);

      toast.success("Programación generada correctamente.");
      onClose?.();
    } catch (error) {
      console.error("Error generando la descarga masiva de programación:", error);
      toast.error(error?.response?.data?.msg || "No se pudo generar la programación.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      aria-labelledby="descargar-programacion-pdf-title"
      open={open}
      onClose={closeIfIdle}
      className="flex items-center justify-center p-4"
    >
      <Sheet
        variant="outlined"
        className="w-full max-w-xl rounded-md bg-white p-6 shadow-lg"
        style={{ maxHeight: "85vh", overflowY: "auto" }}
      >
        <ModalClose variant="plain" className="m-1" onClick={closeIfIdle} />

        <Typography
          component="h2"
          id="descargar-programacion-pdf-title"
          level="h4"
          className="mb-1 text-xl font-black text-slate-900"
        >
          Descargar programación PDF
        </Typography>
        <p className="mb-4 text-sm font-medium text-slate-500">
          Selecciona los deportes colectivos y las fases a incluir. Cada deporte genera un solo PDF
          (grupos + final se combinan en uno); si el resultado son varios PDFs, se descargan en un ZIP.
        </p>

        {loadingSports ? (
          <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700">
            Cargando deportes...
          </div>
        ) : sports.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-600">
            Este evento no tiene deportes colectivos configurados.
          </div>
        ) : (
          <>
            <label className="mb-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                disabled={downloading}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              Seleccionar todos (deportes y fases)
            </label>

            <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
              {sports.map((sport) => {
                const sel = selections[sport.idsport] || emptySelection();
                const sportSelected = sel.groups || sel.finals;

                return (
                  <div
                    key={sport.idsport}
                    className={`rounded-lg border px-3 py-2 ${
                      sportSelected ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white"
                    }`}
                  >
                    <label className="flex cursor-pointer items-center gap-2 text-sm font-bold text-slate-800">
                      <input
                        type="checkbox"
                        checked={sportSelected}
                        onChange={() => toggleSportParent(sport.idsport)}
                        disabled={downloading}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      {sport.displayName || sport.name}
                    </label>

                    <div className="ml-6 mt-1 flex flex-wrap gap-4">
                      <label
                        className={`flex cursor-pointer items-center gap-1.5 text-xs font-semibold ${
                          sportSelected ? "text-slate-700" : "text-slate-400"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={sel.groups}
                          onChange={() => toggleSportPhase(sport.idsport, "groups")}
                          disabled={downloading}
                          className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        Fase de grupos
                      </label>
                      <label
                        className={`flex cursor-pointer items-center gap-1.5 text-xs font-semibold ${
                          sportSelected ? "text-slate-700" : "text-slate-400"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={sel.finals}
                          onChange={() => toggleSportPhase(sport.idsport, "finals")}
                          disabled={downloading}
                          className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        Fase final
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={closeIfIdle}
            disabled={downloading}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || selectedCount === 0 || loadingSports}
            className="inline-flex items-center justify-center rounded-md bg-[#2564eb] px-4 py-2 text-sm font-black text-white shadow-sm transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {downloading ? "Generando programación..." : "Descargar"}
          </button>
        </div>
      </Sheet>
    </Modal>
  );
};

export default DescargarProgramacionPdfModal;
